/**
 * Saving the card on the Payment Element path (D73) — the widget's half,
 * kept pure so it is tested without a DOM.
 *
 * The one rule everything here serves: the Element and the PaymentIntent
 * must agree on the card's `setup_future_usage`, or Stripe refuses to
 * confirm and the payment is blocked. So the widget tells the server what
 * the Element ACTUALLY holds — not what the box shows, which can be ahead
 * of an update still in flight or one that failed — and the server mints
 * the intent to match. Whether the card is then kept is the server's
 * decision, never this one.
 */

/** What the Element was built or last updated with. Undefined = never set. */
export type ElementCardSetup = 'off_session' | 'none' | undefined;

/** Methods a card option can never keep. The box is hidden for them, its tick kept. */
const NOT_A_CARD = new Set(['klarna', 'affirm', 'afterpay_clearpay']);

/**
 * Show the box while the chosen method could be a card. Wallets (Apple Pay,
 * Google Pay) pay as cards, so anything that isn't a known non-card method
 * shows it — including before the Element has reported a choice.
 */
export function showSaveCardBox(offered: boolean, selectedType: string | null): boolean {
  return offered && !(selectedType && NOT_A_CARD.has(selectedType));
}

/** The Element option for a tick. */
export function cardSetupFor(ticked: boolean): 'off_session' | 'none' {
  return ticked ? 'off_session' : 'none';
}

/**
 * What to send with the intent request. Undefined when the Element was
 * never given a card option — the request then looks exactly like an older
 * widget's, and the server sends none either.
 */
export function saveCardRequest(
  held: ElementCardSetup,
  policyText: string | null,
): { accepted: boolean; policyTextSeen: string | null } | undefined {
  if (held === undefined) return undefined;
  return { accepted: held === 'off_session', policyTextSeen: held === 'off_session' ? policyText : null };
}

/**
 * May the client pay now? Only when the Element holds what the box says.
 * An update Stripe rejected leaves the OLD setting in place — after an
 * untick that would keep a card the client just declined. "Never set" with
 * nothing ticked is the one agreed unset state.
 */
export function cardSetupAgrees(held: ElementCardSetup, wantKept: boolean): boolean {
  const want = cardSetupFor(wantKept);
  return held === want || (held === undefined && want === 'none');
}
